<script setup lang="ts">
import { useLocationStore } from '@/stores/useLocationStore'
import DropdownComponent from '@/components/DropdownComponent.vue'
import { useNormalizedFilter } from '@/composables/useNormalizedFilter'
import { useDebounceFn } from '@vueuse/core'


const locationStore = useLocationStore()

const {
  filteredItems: filteredDistricts,
  setSearchQuery: setDistrictSearchQuery,
  clearSearchQuery: clearDistrictSearchQuery,
} = useNormalizedFilter(
  () => locationStore.districts,
  (district) => district,
)

const {
  filteredItems: filteredTowns,
  setSearchQuery: setTownSearchQuery,
  clearSearchQuery: clearTownSearchQuery,
} = useNormalizedFilter(
  () => locationStore.towns,
  (town) => town,
)

const debouncedFetchStreets = useDebounceFn((query: string) => {
  if (locationStore.selectedTown && (query.length >= 3 || query.length === 0)) {
    locationStore.fetchStreets(query).catch(() => {})
  }
}, 300)

const clearStreets = (): void => {
  locationStore.resultData = null
}

</script>

<template>
  <DropdownComponent
    class="district-select"
    v-model="locationStore.selectedDistrict"
    :options="filteredDistricts"
    :disabled="!locationStore.selectedRegion"
    :loading="locationStore.districtsIsLoading"
    :onClear="clearDistrictSearchQuery"
    placeholder="Vyberte okres"
    aria-label="Vyberte okres"
    @search-change="setDistrictSearchQuery"
  />

  <DropdownComponent
    class="town-select"
    v-model="locationStore.selectedTown"
    :options="filteredTowns"
    :disabled="!locationStore.selectedDistrict"
    :loading="locationStore.townsIsLoading"
    :onClear="clearTownSearchQuery"
    placeholder="Vyberte obec"
    aria-label="Vyberte obec"
    @search-change="setTownSearchQuery"
  />

  <DropdownComponent
    class="street-select"
    v-model="locationStore.selectedStreet"
    :options="locationStore.streets"
    :disabled="!locationStore.selectedTown"
    :loading="locationStore.streetsIsLoading"
    label="street"
    track-by="street_numeric_code"
    :clear-value="null"
     :onClear="clearStreets"
    placeholder="Vyberte ulicu"
    aria-label="Vyberte ulicu"
    @search-change="debouncedFetchStreets">
    <template #singleLabel="{ option }">
      <strong>{{ option.street }}</strong>
    </template>
  </DropdownComponent>
</template>

<style scoped>
.street-select,
.town-select,
.district-select {
  width: 100%;
}
</style>
